import React, { useState } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Dog, Crown, Check, Lock, Zap, Sparkles, ArrowRight, ChartBar, Shield, Info
} from "lucide-react";
import { motion } from 'framer-motion';
import { useUser } from "@clerk/clerk-react";
import toast from 'react-hot-toast';
import axios from 'axios';
import { cn } from "@/lib/utils";

const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
        <motion.div
            className="relative w-5 h-5"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
            <div className="absolute w-full h-full border-2 border-amber-200 border-t-amber-500 rounded-full" />
            <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{ scale: [1, 0.8, 1] }}
                transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
            >
                <div className="w-2 h-2 bg-amber-500 rounded-full" />
            </motion.div>
        </motion.div>
    </div>
);

const notify = {
    loading: (message) =>
        toast.loading(message, {
            style: {
                background: '#FEF3C7',
                color: '#92400E',
                padding: '16px',
                borderRadius: '8px',
            },
            iconTheme: {
                primary: '#92400E',
                secondary: '#FEF3C7',
            },
        }),
    success: (message) =>
        toast.success(message, {
            style: {
                background: '#F0FDF4',
                color: '#166534',
                padding: '16px',
                borderRadius: '8px',
            },
            duration: 3000,
        }),
    error: (message) =>
        toast.error(message, {
            style: {
                background: '#FEF2F2',
                color: '#991B1B',
                padding: '16px',
                borderRadius: '8px',
            },
            duration: 4000,
        }),
};

const PLAN_CONFIGS = {
    Basic: {
        name: "Básico",
        price: "49",
        icon: Dog,
        description: "Ideal para el cuidado esencial de tu mascota",
        features: [
            { text: "5 reportes mensuales", highlight: true },
            { text: "Reporte completo: Salud, Nutrición y Cuidados", highlight: true },
            { text: "Historial de análisis", highlight: true },
            { text: "10% descuento en consultas veterinarias online", highlight: false },
        ],
        color: "text-blue-600",
        bgGradient: "from-blue-500 to-blue-600",
        bgHover: "bg-blue-50"
    },
    Pro: {
        name: "Pro",
        price: "99",
        icon: Zap,
        description: "Para hogares con más de una mascota",
        features: [
            { text: "15 reportes mensuales", highlight: true },
            { text: "Reporte completo: Salud, Nutrición y Cuidados", highlight: true },
            { text: "Historial de análisis", highlight: true },
            { text: "Soporte prioritario", highlight: true },
            { text: "20% descuento en consultas veterinarias online", highlight: false },
        ],
        color: "text-amber-600",
        bgGradient: "from-amber-500 to-orange-500",
        bgHover: "bg-amber-50",
        recommended: true
    },
    Premium: {
        name: "Premium",
        price: "149",
        icon: Crown,
        description: "Para veterinarios y mascotas especiales",
        features: [
            { text: "Reportes ilimitados", highlight: true },
            { text: "Reporte completo: Salud, Nutrición y Cuidados", highlight: true },
            { text: "Historial de análisis", highlight: true },
            { text: "Soporte 24/7 personalizado", highlight: true },
            { text: "Ideal para veterinarios y refugios", highlight: true },
            { text: "Acceso anticipado a nuevas funciones", highlight: true },
            { text: "30% descuento en consultas veterinarias online", highlight: false },
        ],
        color: "text-purple-600",
        bgGradient: "from-purple-500 to-purple-600",
        bgHover: "bg-purple-50"
    }
};

const UpgradeModalReport = ({ isOpen, onClose, plan = 'Free' }) => {
    const [isCancelling, setIsCancelling] = useState(false);
    const { user } = useUser();

    const getButtonText = (planKey) => {
        if (planKey === plan) return "Plan Actual";
        return "Seleccionar Plan";
    };

    const handleSubscriptionManagement = async (planConfig) => {
        const loadingToast = notify.loading('Procesando tu solicitud...');
        try {
            setIsCancelling(true);
            const userEmail = user?.primaryEmailAddress?.emailAddress;

            const response = await axios.post('/api/checkout', {
                userEmail,
                userId: user?.id,
                name: planConfig.name
            });

            if (response.data?.url) {
                toast.dismiss(loadingToast);
                notify.success('¡Todo listo! Redirigiendo al checkout...');

                setTimeout(() => {
                    window.location.assign(response.data.url);
                }, 1000);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.dismiss(loadingToast);
            notify.error('No se pudo procesar el pago. Por favor, intenta de nuevo.');
        } finally {
            setIsCancelling(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[95vw] md:max-w-[85vw] lg:max-w-[1000px] h-[90vh] p-0 overflow-hidden">
                <div className="relative bg-gradient-to-br from-amber-50/50 via-orange-50/30 to-white p-3 sm:p-4 h-full overflow-y-auto">
                    {/* Background Effects */}
                    <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-amber-100/20 to-transparent -z-10" />
                    <div className="absolute top-10 right-10 w-72 h-72 bg-orange-200/10 rounded-full blur-3xl -z-10" />
                    <div className="absolute bottom-10 left-10 w-72 h-72 bg-amber-200/10 rounded-full blur-3xl -z-10" />

                    {/* Header */}
                    <div className="text-center mb-4 sm:mb-6 px-2 sm:px-0">
                        <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full mb-3 shadow-sm">
                            <Sparkles className="h-4 w-4 text-amber-600 animate-pulse" />
                            <span className="text-sm font-medium bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                Tecnología AI de Última Generación
                            </span>
                        </div>

                        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                            El cuidado inteligente que tu mascota merece
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-600 max-w-md mx-auto mb-4">
                            Elige el plan ideal y dale a tu mejor amigo un cuidado personalizado y preventivo
                        </p>

                        {/* Feature Badges */}
                        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                            {[
                                { icon: Shield, text: "Tecnología AI" },
                                { icon: ChartBar, text: "90% de Precisión" },
                                { icon: Zap, text: "Resultados Instantáneos" }
                            ].map((feature, index) => (
                                <div key={index} className="flex items-center gap-1.5 sm:gap-2 bg-white/90 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 rounded-lg shadow-sm border border-amber-100/30 hover:border-amber-200/50 transition-all duration-300 hover:scale-105">
                                    <feature.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600" />
                                    <span className="text-xs sm:text-sm font-medium text-gray-700">{feature.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Plans Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 max-w-[1000px] mx-auto">
                        {Object.entries(PLAN_CONFIGS).map(([planKey, planConfig]) => {
                            const isCurrent = planKey === plan;
                            return (
                                <div key={planKey}
                                    className={cn(
                                        "bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 shadow-lg border-2 flex flex-col h-full",
                                        "hover:shadow-xl transition-all duration-300 group",
                                        planConfig.recommended
                                            ? "border-amber-200 hover:border-amber-300/80"
                                            : "border-gray-100/80 hover:border-gray-200/80",
                                        planConfig.recommended && "relative"
                                    )}>
                                    {planConfig.recommended && (
                                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                                            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-0.5 rounded-full text-xs font-medium shadow-md ring-2 ring-white">
                                                Más Popular
                                            </span>
                                        </div>
                                    )}

                                    {/* Plan Content */}
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 sm:gap-2.5 mb-2 sm:mb-3">
                                            <div className={cn(
                                                "p-2 rounded-lg transition-transform duration-300 group-hover:scale-110",
                                                planConfig.bgHover
                                            )}>
                                                <planConfig.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-sm sm:text-base">{planConfig.name}</h3>
                                                <p className="text-xs text-gray-500">{planConfig.description}</p>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="flex items-baseline">
                                                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                                    ${planConfig.price}
                                                </span>
                                                <span className="text-xs sm:text-sm text-gray-500">/mes</span>
                                            </div>
                                        </div>

                                        {/* Features */}
                                        <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                                            {planConfig.features.map((feature, index) => (
                                                <li key={index} className="flex items-start gap-1.5 sm:gap-2 group/item">
                                                    <div className={cn(
                                                        "rounded-full p-0.5 transition-colors duration-200",
                                                        feature.highlight ? "bg-emerald-100" : "bg-amber-100",
                                                        "group-hover/item:scale-110 transition-transform duration-200"
                                                    )}>
                                                        {feature.highlight ? (
                                                            <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-600" />
                                                        ) : (
                                                            <Info className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-amber-600" />
                                                        )}
                                                    </div>
                                                    <span className="text-xs sm:text-sm text-gray-600 leading-tight">
                                                        {feature.text}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Button */}
                                    <Button
                                        onClick={() => !isCurrent && handleSubscriptionManagement(planConfig)}
                                        disabled={isCurrent || isCancelling}
                                        className={cn(
                                            "w-full h-9 sm:h-10 bg-gradient-to-r text-white font-medium text-sm",
                                            "shadow-sm hover:shadow-md transition-all duration-300",
                                            "hover:translate-y-[-1px]",
                                            `bg-gradient-to-r ${planConfig.bgGradient}`,
                                            isCurrent && "opacity-75 cursor-not-allowed"
                                        )}
                                    >
                                        {isCancelling ? (
                                            <LoadingSpinner />
                                        ) : (
                                            <span className="flex items-center justify-center gap-1">
                                                {getButtonText(planKey)}
                                                {!isCurrent && (
                                                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                                                )}
                                            </span>
                                        )}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="mt-4 sm:mt-5 text-center">
                        <div className="inline-flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-gray-100/80">
                            <Lock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-600" />
                            <span className="text-xs text-gray-600">Pago seguro · Cancela cuando quieras</span>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UpgradeModalReport;